import React from 'react';
import { useSelector } from 'react-redux';

import { usePermissions } from '@/app/hooks/usePermissions';
import { FsmState } from '@/modules/onboarding/fsm-types';
import type { TFsmState } from '@/modules/onboarding/fsm-types';
import { OnboardingStep, IPartner, TOnboardingStep } from '@/modules/partner/model';
import { getPartner } from '@/modules/partner/store/selectors';
import { MainLayout } from '@/shared/view/MainLayout';

import { useCongratsDialog } from './components/Congrats';
import { WizardManager } from './manager';

const onboardingStepToCurrentFsmState: Record<TOnboardingStep, TFsmState> = {
  [OnboardingStep.welcome]: FsmState.welcome,
  [OnboardingStep.hello]: FsmState.driver,
  [OnboardingStep.payouts]: FsmState.payTo,
  [OnboardingStep.payment]: FsmState.partnerPayments,
};

const onboardingStepToNextFsmState: Record<TOnboardingStep, TFsmState> = {
  [OnboardingStep.welcome]: FsmState.driver,
  [OnboardingStep.hello]: FsmState.payTo,
  [OnboardingStep.payouts]: FsmState.partnerPayments,
  [OnboardingStep.payment]: FsmState.doYouHaveCar,
};

const onboardingStepToNextFsmStateUK: Record<TOnboardingStep, TFsmState> = {
  [OnboardingStep.welcome]: FsmState.licensingAuthority,
  [OnboardingStep.hello]: FsmState.payTo,
  [OnboardingStep.payouts]: FsmState.partnerPayments,
  [OnboardingStep.payment]: FsmState.toDo,
};

const onboardingStepToNextFsmStateFR: Record<TOnboardingStep, TFsmState> = {
  [OnboardingStep.welcome]: FsmState.vehicle,
  [OnboardingStep.hello]: FsmState.payTo,
  [OnboardingStep.payouts]: FsmState.partnerPayments,
  [OnboardingStep.payment]: FsmState.toDo,
};

const onboardingStepToNextFsmStateUS: Record<TOnboardingStep, TFsmState> = {
  [OnboardingStep.welcome]: FsmState.vehicle,
  [OnboardingStep.hello]: FsmState.payTo,
  [OnboardingStep.payouts]: FsmState.partnerPayments,
  [OnboardingStep.payment]: FsmState.toDo,
};

const getInitialFsmValue = (partner: IPartner): TFsmState => {
  if (partner.onboarding.isCompleted) {
    return FsmState.toDo;
  }

  const lastStepName = partner.onboarding.lastStepName;

  if (partner.onboarding.isLastStepCompleted) {
    if (partner.countryCode === 'GB') {
      return onboardingStepToNextFsmStateUK[lastStepName];
    }

    if (partner.countryCode === 'FR') {
      return onboardingStepToNextFsmStateFR[lastStepName];
    }

    if (partner.countryCode === 'US') {
      return onboardingStepToNextFsmStateUS[lastStepName];
    }

    return onboardingStepToNextFsmState[lastStepName];
  }

  return onboardingStepToCurrentFsmState[lastStepName];
};

const Wizard: React.FC = React.memo(() => {
  usePermissions('viewWizard');

  const partner = useSelector(getPartner);

  useCongratsDialog();

  return (
    <MainLayout>
      <WizardManager
        partnerFirstName={partner.firstName}
        partnerCountryCode={partner.countryCode}
        initialValue={getInitialFsmValue(partner)}
      />
    </MainLayout>
  );
});

Wizard.displayName = 'Wizard';

export { Wizard };
